import type { RefObject, KeyboardEvent } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Returns an `onKeyDown` handler that traps Tab / Shift+Tab within a container.
 * Attach to the container element's `onKeyDown` prop.
 *
 * Reusable across modals and drawers.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  isOpen: boolean,
): (e: KeyboardEvent) => void {
  return (e: KeyboardEvent) => {
    if (!isOpen) return;
    if (e.key !== "Tab") return;

    const focusable =
      containerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
    if (!focusable || focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };
}
